/**
 * Règles métier Retail (Messika).
 *
 * Une seule grille (RETAIL_GRID) — copie 1:1 de DocRetail/GRILLE_ECR_ECF.txt —
 * indexée par `${zone}|${depot}|${type_client}|${scope}` et donnant pour
 * chaque cas autorisé :
 *   - le type de commande (ECR ou ECF) à créer par "Créer commande Retail"
 *   - le %age de remise (0 ou 100) par famille (ECRINS, OAV, PACKAGING, GIFTS),
 *     ou `null` si la famille est interdite (catalogue masqué)
 *
 * Tout cas absent de la grille est interdit. Les Wholesale (WHS, codes 050/060)
 * ne sont pas gérés pour le moment et ne figurent pas dans la grille.
 *
 * Codes utilisés :
 *   - glob_const.customer_groups (010..080 : type métier × scope group/filiale)
 *   - glob_const.geo_zones (010..070 : zones géo)
 *   - retail_familles AppWrite (80..85 : catégories retail)
 */

import glob_const from './glob_const'

// ─── Constantes familles ────────────────────────────────────────────────────

/** Codes retail_familles utilisés par la grille (cf. retail_familles.json M3). */
export const FAM_ECRINS = '80'
export const FAM_GIFTS = '81'
export const FAM_OAV = '82'
export const FAM_MOBILIER = '83'   // hors grille → invisible/non remisable pour tous
export const FAM_PLV = '84'         // hors grille → invisible/non remisable pour tous
export const FAM_PACKAGING = '85'

/** Familles couvertes par la grille (les seules visibles côté retail). */
export const RETAIL_FAMS = [FAM_ECRINS, FAM_OAV, FAM_PACKAGING, FAM_GIFTS] as const

export type OrderType = 'ECR' | 'ECF'
export type RetailFamille = typeof RETAIL_FAMS[number]
/**
 * Remise applicable à une famille selon la grille :
 *   - 0    : facturé plein tarif (pas de remise)
 *   - 100  : remise 100% (= vraie remise comptable, affichée à l'utilisateur)
 *   - 'NF' : "Non Facturé" — gratuit côté oMe (affichage), mais discount=0 stocké
 *            (M3 valorise plein tarif sans facturer le client, traitement interne)
 *   - null : famille interdite (catalogue masqué)
 */
export type Discount = 0 | 100 | 'NF' | null

export interface RetailRule {
   orderType: OrderType
   discounts: Record<RetailFamille, Discount>
}

// ─── Patterns de remise réutilisables ───────────────────────────────────────

/** ECR : tout "Non Facturé" (Retail Interne et Interco "interne") — affiché gratuit côté oMe, discount=0 vers M3. */
const ECR_ALL_NF: RetailRule = {
   orderType: 'ECR',
   discounts: { [FAM_ECRINS]: 'NF', [FAM_OAV]: 'NF', [FAM_PACKAGING]: 'NF', [FAM_GIFTS]: 'NF' },
}

/** ECF : tout facturé (Interco facturé, Retail Interne hors interne). */
const ECF_ALL_BILLED: RetailRule = {
   orderType: 'ECF',
   discounts: { [FAM_ECRINS]: 0, [FAM_OAV]: 0, [FAM_PACKAGING]: 0, [FAM_GIFTS]: 0 },
}

/** ECF : OAV facturé, PACKAGING offert, ECRINS+GIFTS interdits (Retail Externe). */
const ECF_OAV_PACK: RetailRule = {
   orderType: 'ECF',
   discounts: { [FAM_ECRINS]: null, [FAM_OAV]: 0, [FAM_PACKAGING]: 100, [FAM_GIFTS]: null },
}

// ─── Grille ECR/ECF + remises ───────────────────────────────────────────────
//
// Clé : `${zone}|${depot}|${type_client}|${scope}`
//   - zone : code zone géo M3 (010..070)
//   - depot : code dépôt retail (SFR, SUS, SMO, SCN, SJP)
//   - type_client : RET | ERE | INT (WHS non géré)
//   - scope : group | filiale
//
// Toute clé absente = combinaison interdite. Volontairement verbeuse pour
// rester 1:1 avec DocRetail/GRILLE_ECR_ECF.txt.

const RETAIL_GRID: Record<string, RetailRule> = {
   // ─── EUROPE 010 ──────────────────────────────────────────────────────────
   '010|SFR|RET|group':   ECR_ALL_NF,
   '010|SFR|ERE|group':   ECF_OAV_PACK,
   '010|SFR|INT|group':   ECF_ALL_BILLED,

   // ─── APAC 070 ────────────────────────────────────────────────────────────
   '070|SFR|RET|filiale': ECF_ALL_BILLED,
   '070|SFR|ERE|filiale': ECF_OAV_PACK,
   '070|SCN|RET|filiale': ECF_ALL_BILLED,
   '070|SCN|ERE|filiale': ECF_OAV_PACK,

   // ─── USA 050 ─────────────────────────────────────────────────────────────
   '050|SFR|INT|group':   ECF_ALL_BILLED,
   '050|SUS|RET|filiale': ECR_ALL_NF,
   '050|SUS|ERE|filiale': ECF_OAV_PACK,
   '050|SUS|INT|filiale': ECR_ALL_NF,

   // ─── AMER 040 ────────────────────────────────────────────────────────────
   '040|SFR|ERE|group':   ECF_OAV_PACK,
   '040|SFR|ERE|filiale': ECF_OAV_PACK,

   // ─── MO 060 ──────────────────────────────────────────────────────────────
   '060|SFR|ERE|group':   ECF_OAV_PACK,
   '060|SMO|ERE|group':   ECF_OAV_PACK,
   '060|SCN|ERE|group':   ECF_OAV_PACK,

   // ─── JAPON 020 ───────────────────────────────────────────────────────────
   '020|SFR|RET|filiale': ECF_ALL_BILLED,
   '020|SFR|ERE|filiale': ECF_OAV_PACK,
   '020|SFR|INT|group':   ECF_ALL_BILLED,
   '020|SFR|INT|filiale': ECF_ALL_BILLED,
   '020|SJP|RET|filiale': ECR_ALL_NF,
   '020|SJP|ERE|filiale': ECF_OAV_PACK,
   '020|SJP|INT|filiale': ECR_ALL_NF,
   '020|SCN|RET|filiale': ECF_ALL_BILLED,
   '020|SCN|ERE|filiale': ECF_OAV_PACK,
   '020|SCN|INT|filiale': ECF_ALL_BILLED,

   // ─── CHINE 030 ───────────────────────────────────────────────────────────
   '030|SFR|RET|filiale': ECF_ALL_BILLED,
   '030|SFR|ERE|filiale': ECF_OAV_PACK,
   '030|SFR|INT|group':   ECF_ALL_BILLED,
   '030|SFR|INT|filiale': ECF_ALL_BILLED,
   '030|SCN|RET|filiale': ECR_ALL_NF,
   '030|SCN|ERE|filiale': ECF_OAV_PACK,
   '030|SCN|INT|filiale': ECR_ALL_NF,
}

// ─── Types & helpers ────────────────────────────────────────────────────────

interface CustomerGroupEntry {
   type: 'RET' | 'ERE' | 'INT'
   scope: 'group' | 'filiale'
   label: string
   has_quota: boolean
}

const CUSTOMER_GROUPS = (glob_const as { customer_groups: Record<string, CustomerGroupEntry> }).customer_groups

/** Vue minimale d'un customer suffisante pour interroger la grille retail. */
export interface RetailCustomerLike {
   groupe_client?: string | null
   depot_retail?: string | null
   zone_geo?: string | null
}

/** Récupère le code groupe_client M3 d'un customer (champ `groupe_client`). */
export function customerGroupCode(customer: RetailCustomerLike | null | undefined): string | null {
   const code = customer?.groupe_client
   return code && CUSTOMER_GROUPS?.[code] ? code : null
}

/**
 * Retourne la règle retail (type de commande + remises par famille) applicable
 * à ce customer, ou null si aucune commande retail n'est possible pour lui.
 */
export function getRetailRule(customer: RetailCustomerLike | null | undefined): RetailRule | null {
   if (!customer) return null
   const grp = CUSTOMER_GROUPS?.[customer.groupe_client ?? '']
   if (!grp) return null
   const depot = customer.depot_retail
   const zone = customer.zone_geo
   if (!depot || !zone) return null
   return RETAIL_GRID[`${zone}|${depot}|${grp.type}|${grp.scope}`] ?? null
}

/** Indique si une commande retail peut être créée pour ce customer. */
export function canCreateRetailOrder(customer: RetailCustomerLike | null | undefined): boolean {
   return getRetailRule(customer) !== null
}

/** Type de commande retail (ECR ou ECF) à créer pour ce customer, ou null si impossible. */
export function getRetailOrderType(customer: RetailCustomerLike | null | undefined): OrderType | null {
   return getRetailRule(customer)?.orderType ?? null
}

/**
 * Remise applicable à une famille pour ce customer (cf. grille) :
 *   - 0, 100, 'NF' si la famille est autorisée
 *   - null si la famille est interdite OU si le customer ne peut pas commander
 *
 * Pour un customer null/admin (sans client courant), retourne 0 si la famille
 * fait partie de RETAIL_FAMS (visible mais non remisée par défaut), sinon null.
 */
export function getRetailFamilleDiscount(
   retail_famille_code: string | null | undefined,
   customer: RetailCustomerLike | null | undefined,
): Discount {
   if (!retail_famille_code) return null
   if (!(RETAIL_FAMS as readonly string[]).includes(retail_famille_code)) return null
   if (!customer) return 0
   const rule = getRetailRule(customer)
   if (!rule) return null
   return rule.discounts[retail_famille_code as RetailFamille]
}

/**
 * Indique si la famille apparaît comme "gratuite" côté oMe pour ce customer.
 *   - true si grille = 100 ou 'NF'
 *   - false sinon (0, null, customer null, famille hors RETAIL_FAMS)
 * Utilisé pour l'affichage catalogue / picker / fiche article.
 */
export function isFreeForCustomer(
   retail_famille_code: string | null | undefined,
   customer: RetailCustomerLike | null | undefined,
): boolean {
   const d = getRetailFamilleDiscount(retail_famille_code, customer)
   return d === 100 || d === 'NF'
}

/**
 * Discount à stocker sur la ligne (Dexie + AppWrite → M3) pour ce customer :
 *   - 100  pour grille = 100 (vraie remise comptable)
 *   - 0    pour grille = 0 ou 'NF' (M3 valorise plein tarif ; pour 'NF' le client
 *          n'est pas facturé pour autant, c'est un traitement comptable interne)
 *   - null si famille interdite (à ne pas commander)
 */
export function getStoredDiscount(
   retail_famille_code: string | null | undefined,
   customer: RetailCustomerLike | null | undefined,
): 0 | 100 | null {
   const d = getRetailFamilleDiscount(retail_famille_code, customer)
   if (d === null) return null
   if (d === 100) return 100
   return 0   // 0 ou 'NF' → stocké 0
}

/**
 * Règle du blindage de la remise d'une ligne (hors lignes commentaire).
 *   - ECR : la grille s'impose toujours (null → 0)
 *   - ECF :
 *       grille = 100                    → 100 (gratuit forcé)
 *       remise numérique déjà présente  → conservée (remise manuelle)
 *       sinon                           → grille, ou 0
 * Une remise retirée (0 / champ vidé) doit être transmise ici comme `undefined`
 * par l'appelant pour retomber sur la grille.
 */
export function resolveLineDiscount(
   orderType: string | null | undefined,
   gridDiscount: number | null | undefined,
   currentDiscount: unknown,
): number {
   if (orderType !== 'ECF') return gridDiscount ?? 0
   if (gridDiscount === 100) return 100
   if (typeof currentDiscount === 'number') return currentDiscount
   return gridDiscount ?? 0
}

/**
 * Indique si une famille retail est visible au catalogue pour ce customer.
 *
 * Pour un customer null/admin/N1/N2 sans client courant : toutes les familles
 * listées dans RETAIL_FAMS sont visibles (Mobilier/PLV restent invisibles).
 *
 * Pour un customer concret : visible ssi la grille donne une remise (0 ou 100)
 * non-null pour cette famille.
 */
export function isVisibleRetailFamille(
   retail_famille_code: string | null | undefined,
   customer: RetailCustomerLike | null | undefined,
): boolean {
   if (!retail_famille_code) return false
   if (!(RETAIL_FAMS as readonly string[]).includes(retail_famille_code)) return false
   if (!customer) return true
   const rule = getRetailRule(customer)
   if (!rule) return false
   return rule.discounts[retail_famille_code as RetailFamille] !== null
}
